import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { PublicLayout } from '../components/layout/PublicLayout';
import { CinematicHomeHero } from '../components/landing/CinematicHomeHero';
import { LandingBelowHero } from '../components/landing/LandingBelowHero';
import { treeHref } from '../utils/prettyArchiveLinks';
import '../styles/product-demo.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SHOWCASE_MODE = process.env.REACT_APP_SHOWCASE_MODE === 'true';
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [treeName, setTreeName] = useState('');
  const [treeDescription, setTreeDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreateTree = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/register');
      return;
    }
    if (!treeName.trim()) {
      toast.error('Please enter a name for your tree');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/trees`, {
        name: treeName,
        description: treeDescription,
      });
      toast.success('Your family tree has been created');
      setIsCreateDialogOpen(false);
      setTreeName('');
      setTreeDescription('');
      navigate(treeHref(response.data));
    } catch (error) {
      toast.error('Failed to create tree. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const openCreateDialog = () => {
    if (!user) {
      navigate('/register');
    } else {
      setIsCreateDialogOpen(true);
    }
  };

  useEffect(() => {
    const hash = window.location.hash?.replace(/^#/, '');
    if (!hash) return undefined;
    const scrollToHash = () => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const timer = window.setTimeout(scrollToHash, 120);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <PublicLayout className="bg-[hsl(42_28%_96%)] text-foreground">
      <div className="noise-overlay fixed inset-0 pointer-events-none z-[9998]" aria-hidden="true" />

      <CinematicHomeHero
        onPrimaryClick={openCreateDialog}
        user={user}
        showcaseMode={SHOWCASE_MODE}
      />

      <LandingBelowHero openCreateDialog={openCreateDialog} />

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="create-tree-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Start Your Family Tree</DialogTitle>
            <DialogDescription>
              Give your tree a name. You can add people and stories once it&apos;s created.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTree}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="tree-name">Tree name</Label>
                <Input
                  id="tree-name"
                  placeholder="e.g., The Williams Family"
                  value={treeName}
                  onChange={(e) => setTreeName(e.target.value)}
                  data-testid="create-tree-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tree-description" className="text-muted-foreground">
                  Description (optional)
                </Label>
                <Textarea
                  id="tree-description"
                  placeholder="Any context you'd like to remember..."
                  value={treeDescription}
                  onChange={(e) => setTreeDescription(e.target.value)}
                  rows={3}
                  className="resize-none"
                  data-testid="create-tree-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                className="w-full rounded-full"
                disabled={creating}
                data-testid="create-tree-submit-btn"
              >
                {creating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Tree'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
};

export default LandingPage;
